import { useTranslation } from 'react-i18next';
import { cn } from '@kidshome/ui';
import type { OccurrenceResponseStatus } from '@kidshome/api-contract';
import { StateIcon } from '@/shared/ui/icons';

/**
 * Occurrence-status pill. Kind copy (no shaming): `missed` reads "Another day", `rejected` reads
 * "Try again". Colors resolve to semantic tokens only — `done`→success, `pending`→warning,
 * `planned`/`skipped`/`missed`→muted, with the lavender accent reused for the rotation state.
 */
const STYLES: Record<OccurrenceResponseStatus, string> = {
  approved: 'bg-success-fg text-success',
  pending_approval: 'bg-warning-fg text-warning',
  planned: 'bg-surface-muted text-text-muted',
  skipped: 'bg-surface-muted text-text-muted',
  missed: 'bg-surface-muted text-text-muted',
  rejected: 'bg-warning-fg text-warning',
};

export function StatePill({
  status,
  rotates = false,
  className,
}: {
  status: OccurrenceResponseStatus;
  rotates?: boolean;
  className?: string;
}): React.ReactElement {
  const { t } = useTranslation('family');
  if (rotates && status === 'planned') {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-pill bg-surface-muted px-2.5 py-1 text-xs font-semibold text-primary',
          className,
        )}
      >
        <StateIcon status={status} rotates size={14} />
        {t('status.rotates')}
      </span>
    );
  }
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-pill px-2.5 py-1 text-xs font-semibold',
        STYLES[status],
        className,
      )}
    >
      <StateIcon status={status} size={14} />
      {t(`status.${status}`)}
    </span>
  );
}
